import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Checkbox,
    Collapse,
    FormControlLabel,
    CircularProgress,
    Grid,
    IconButton,
    Tab,
    Tabs,
    TextField,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Formik } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import { useLoaderData, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { wrapFormikHandleChange } from "../../../../../infrastructure/utils/formatters";

import DefaultApiService from "../../../../../infrastructure/api/default";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import DropdownPicker from "../../../../components/shared/dropdown";
import TabPanel from "../../../../components/shared/tab-panel";

function a11yProps(index) {
  return { id: `supervisor-tab-${index}`, "aria-controls": `supervisor-tabpanel-${index}` };
}

const validationSchema = Yup.object().shape({
  firstName: Yup.string().required("لطفا نام را وارد کنید."),
  lastName: Yup.string().required("لطفا نام خانوادگی را وارد کنید."),
  mobile: Yup.string().required("لطفا شماره همراه را وارد کنید."),
  companyId: Yup.number().min(1, "لطفا شرکت توزیع را انتخاب کنید."),
});

const SolarentOperatorSupervisorInfoModifyPage = () => {
  const data = useLoaderData();
  let navigate = useNavigate();
  const { id: roleId } = useParams();

  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [cityPermission, setCityPermission] = useState(data?.cityPermission ?? []);
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [expandedProvinces, setExpandedProvinces] = useState([]);

  useEffect(() => {
    setCityPermission(data?.cityPermission ?? []);
  }, [data?.cityPermission]);

  useEffect(() => {
    const loadBasicInfo = async () => {
      try {
        const [provincesResponse, citiesResponse] = await Promise.all([
          DefaultApiService.getApi("SLN/SolarentOperator/SupervisorInfo/GetProvinces"),
          DefaultApiService.getApi("SLN/SolarentOperator/SupervisorInfo/GetCities"),
        ]);
        if (provincesResponse?.isSuccess && provincesResponse.data) {
          setProvinces(
            provincesResponse.data.map((p) => ({
              id: p.id,
              provinceName: p.title || p.provinceName || "",
            }))
          );
        }
        if (citiesResponse?.isSuccess && citiesResponse.data) {
          setCities(
            citiesResponse.data.map((c) => ({
              id: c.id,
              cityName: c.cityName || "",
              provinceId: c.provinceId,
            }))
          );
        }
      } catch (error) {
        console.error("Error loading provinces/cities for supervisor city permissions", error);
      }
    };
    loadBasicInfo();
  }, []);

  const onNavigateToListPage = () => {
    navigate("/SLN/SolarentOperator/SupervisorInfo");
  };

  const onCheckedCityPermission = (event, city) => {
    const checked = event.target.checked;
    setCityPermission((prev) =>
      prev.map((x) => (x.cityId === city.cityId ? { ...x, allowed: checked } : x))
    );
  };

  const onHandleAllCheckedCity = (isChecked) => {
    setCityPermission((prev) => prev.map((x) => ({ ...x, allowed: isChecked })));
  };

  const onToggleProvinceCities = (provinceId, isChecked) => {
    setCityPermission((prev) =>
      prev.map((x) => {
        const meta = cities.find((c) => c.id === x.cityId);
        if (meta && meta.provinceId === provinceId) return { ...x, allowed: isChecked };
        return x;
      })
    );
  };

  const provinceGroups = useMemo(() => {
    if (!cities.length || !provinces.length || !Array.isArray(cityPermission)) return [];
    const map = new Map();
    cities.forEach((c) => {
      const perm = cityPermission.find((p) => p.cityId === c.id);
      const allowed = perm ? perm.allowed : false;
      let group = map.get(c.provinceId);
      if (!group) {
        const province = provinces.find((p) => p.id === c.provinceId);
        group = {
          provinceId: c.provinceId,
          provinceName: province?.provinceName || `استان ${c.provinceId}`,
          cities: [],
        };
        map.set(c.provinceId, group);
      }
      group.cities.push({ cityId: c.id, cityName: c.cityName, allowed });
    });
    return Array.from(map.values()).sort((a, b) =>
      a.provinceName.localeCompare(b.provinceName, "fa")
    );
  }, [cities, provinces, cityPermission]);

  const isProvinceExpanded = (provinceId) => expandedProvinces.includes(provinceId);
  const toggleProvinceExpanded = (provinceId) => {
    setExpandedProvinces((prev) =>
      prev.includes(provinceId) ? prev.filter((id) => id !== provinceId) : [...prev, provinceId]
    );
  };

  // مقادیر اولیه: API اکنون firstName و lastName را برمی‌گرداند
  const initialValues = useMemo(() => {
    const info = data?.supervisorInfo || {};
    const fullName = info.fullName || "";
    const parts = fullName.trim().split(/\s+/);
    return {
      id: info.id ?? 0,
      userId: info.userId ?? 0,
      companyId: info.companyId ?? 0,
      userName: info.userName ?? "",
      firstName: info.firstName ?? (parts[0] || ""),
      lastName: info.lastName ?? (parts.slice(1).join(" ") || ""),
      mobile: info.mobile ?? "",
      telephon: info.telephon ?? "",
      description: info.description ?? "",
      supervisorInfoStatus: info.supervisorInfoStatus ?? "Pending",
      isActive: info.isActive ?? true,
    };
  }, [data?.supervisorInfo]);

  // Submit Action
  const onSubmit = async (formValue, { setSubmitting }) => {
    setLoading(true);
    const sendData = {
      id: formValue.id,
      userId: formValue.userId,
      companyId: formValue.companyId,
      firstName: formValue.firstName,
      lastName: formValue.lastName,
      mobile: formValue.mobile,
      telephon: formValue.telephon,
      description: formValue.description,
      supervisorInfoStatus: formValue.supervisorInfoStatus,
      isActive: formValue.isActive,
      cityPermission: cityPermission.map((p) => ({ cityId: p.cityId, cityName: p.cityName, allowed: p.allowed })),
    };

    try {
      const res = await DefaultApiService.modifyApi(
        "SLN/SolarentOperator/SupervisorInfo",
        sendData
      );

      if (res.isSuccess) {
        toast.success(res.message);
        onNavigateToListPage();
      } else {
        toast.error(res.message);
      }

      setLoading(false);
    } catch (error) {
      toast.error(error?.response?.data?.message || "خطایی رخ داده است.");

      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={12}>
        <BreadcrumbsComponent />
      </Grid>

      <Grid item xs={12} md={12}>
        <Card>
          <CardHeader
            title={
              roleId !== "create"
                ? `ویرایش ناظر ${data?.supervisorInfo?.fullName ?? ""}`
                : "ایجاد ناظر"
            }
          />

          <CardContent>
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={onSubmit}
              enableReinitialize
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
                setFieldValue,
              }) => {
                const onMakeFormikVariable = (name) => ({
                  name: name,
                  fullWidth: true,
                  value: values[name],
                  error: errors[name] && touched[name],
                  helperText: errors[name] && touched[name] && errors[name],
                  variant: "outlined",
                });

                return (
                  <form onSubmit={handleSubmit}>
                    <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                      <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} aria-label="supervisor tabs">
                        <Tab label="اطلاعات ناظر" {...a11yProps(0)} />
                        <Tab label="دسترسی شهرستان‌ها" {...a11yProps(1)} />
                      </Tabs>
                    </Box>

                    <TabPanel value={tabValue} index={0}>
                      <Grid container spacing={3}>
                        {roleId !== "create" && values.userName && (
                          <Grid item xs={12} md={6} lg={3}>
                            <TextField
                              label="نام کاربری"
                              disabled
                              {...onMakeFormikVariable("userName")}
                            />
                          </Grid>
                        )}
                        <Grid item xs={12} md={6} lg={3}>
                          <TextField
                            label="نام"
                            onChange={wrapFormikHandleChange(handleChange)}
                            {...onMakeFormikVariable("firstName")}
                          />
                        </Grid>
                        <Grid item xs={12} md={6} lg={3}>
                          <TextField
                            label="نام خانوادگی"
                            onChange={wrapFormikHandleChange(handleChange)}
                            {...onMakeFormikVariable("lastName")}
                          />
                        </Grid>
                        <Grid item xs={12} md={6} lg={3}>
                          <TextField
                            label="همراه"
                            onChange={wrapFormikHandleChange(handleChange)}
                            {...onMakeFormikVariable("mobile")}
                          />
                        </Grid>
                        <Grid item xs={12} md={6} lg={3}>
                          <TextField
                            label="تلفن"
                            onChange={wrapFormikHandleChange(handleChange)}
                            {...onMakeFormikVariable("telephon")}
                          />
                        </Grid>
                        <Grid item xs={12} md={6} lg={3}>
                          <DropdownPicker
                            label="شرکت توزیع"
                            onChange={(event) => {
                              setFieldValue("companyId", event.target.value);
                            }}
                            list={data.companyList || []}
                            optionName="companyName"
                            optionValue="id"
                            {...onMakeFormikVariable("companyId")}
                          />
                        </Grid>
                        <Grid item xs={12} md={6} lg={3}>
                          <TextField
                            label="توضیحات"
                            onChange={wrapFormikHandleChange(handleChange)}
                            {...onMakeFormikVariable("description")}
                          />
                        </Grid>
                        <Grid item xs={12} md={6} lg={3}>
                          <DropdownPicker
                            label="وضعیت"
                            onChange={(event) => {
                              setFieldValue(
                                "supervisorInfoStatus",
                                event.target.value
                              );
                            }}
                            list={data.supervisorInfoStatus || []}
                            filterable={false}
                            {...onMakeFormikVariable("supervisorInfoStatus")}
                          />
                        </Grid>
                        <Grid item xs={12} md={6} lg={3}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={Boolean(values.isActive)}
                                onChange={(e) =>
                                  setFieldValue("isActive", e.target.checked)
                                }
                                name="isActive"
                              />
                            }
                            label="فعال"
                          />
                        </Grid>
                      </Grid>
                    </TabPanel>

                    <TabPanel value={tabValue} index={1}>
                      <Grid container spacing={1}>
                        <Grid item xs={12} md={12}>
                          <Button
                            color="primary"
                            variant="contained"
                            size="small"
                            onClick={() => onHandleAllCheckedCity(true)}
                            sx={{ mr: 1 }}
                          >
                            انتخاب همه
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => onHandleAllCheckedCity(false)}
                          >
                            حذف همه
                          </Button>
                        </Grid>
                        {provinceGroups.length > 0
                          ? provinceGroups.map((province) => {
                              const total = province.cities.length;
                              const selectedCount = province.cities.filter((c) => c.allowed).length;
                              const allChecked = total > 0 && selectedCount === total;
                              const someChecked = selectedCount > 0 && selectedCount < total;
                              const expanded = isProvinceExpanded(province.provinceId);
                              return (
                                <Grid item xs={12} key={province.provinceId}>
                                  <Box
                                    sx={{
                                      border: "1px solid #eee",
                                      borderRadius: 1,
                                      p: 1,
                                      mb: 1,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                      }}
                                    >
                                      <FormControlLabel
                                        control={
                                          <Checkbox
                                            checked={allChecked}
                                            indeterminate={someChecked}
                                            onChange={(e) =>
                                              onToggleProvinceCities(
                                                province.provinceId,
                                                e.target.checked
                                              )
                                            }
                                          />
                                        }
                                        label={province.provinceName}
                                      />
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          toggleProvinceExpanded(province.provinceId)
                                        }
                                        aria-expanded={expanded}
                                        aria-label={expanded ? "بستن شهرستان‌ها" : "نمایش شهرستان‌ها"}
                                      >
                                        {expanded ? (
                                          <ExpandLessIcon fontSize="small" />
                                        ) : (
                                          <ExpandMoreIcon fontSize="small" />
                                        )}
                                      </IconButton>
                                    </Box>
                                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                                      <Grid container spacing={1} sx={{ pl: 3, pt: 1, pb: 0.5 }}>
                                        {province.cities.map((city) => (
                                          <Grid
                                            item
                                            xs={12}
                                            md={4}
                                            lg={3}
                                            key={city.cityId}
                                          >
                                            <FormControlLabel
                                              control={
                                                <Checkbox
                                                  checked={city.allowed}
                                                  onChange={(e) =>
                                                    onCheckedCityPermission(e, city)
                                                  }
                                                />
                                              }
                                              label={city.cityName}
                                            />
                                          </Grid>
                                        ))}
                                      </Grid>
                                    </Collapse>
                                  </Box>
                                </Grid>
                              );
                            })
                          : (Array.isArray(cityPermission) && cityPermission.length > 0
                              ? cityPermission.map((city) => (
                                  <Grid item xs={12} md={4} lg={3} key={city.cityId}>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={city.allowed}
                                          onChange={(e) =>
                                            onCheckedCityPermission(e, city)
                                          }
                                        />
                                      }
                                      label={city.cityName}
                                    />
                                  </Grid>
                                ))
                              : null)}
                      </Grid>
                    </TabPanel>

                    <Grid container spacing={3} justifyContent="flex-end" sx={{ mt: 2 }}>
                      <Grid item>
                        <Button
                          disabled={isSubmitting}
                          variant="outlined"
                          size="large"
                          color="gray"
                          sx={{ mx: 1 }}
                          onClick={onNavigateToListPage}
                        >
                          انصراف
                        </Button>
                        <Button
                          disabled={isSubmitting}
                          variant="contained"
                          type="submit"
                          size="large"
                        >
                          {loading && (
                            <CircularProgress size={16} color="inherit" />
                          )}
                          {roleId !== "create" ? "ویرایش" : "ایجاد"}
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                );
              }}
            </Formik>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default SolarentOperatorSupervisorInfoModifyPage;
